// @flow
import * as React from 'react';
import moment from 'moment/moment';
import { Collapse, MenuItem, Stack, Typography } from '@mui/material';
import { SelectInput, TextInput, DateRange } from '@coverright/ui/inputs';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { DateRangeInput } from '@coverright/data-access/types/enrollment';

export type Periodic<T, F extends keyof T> = Omit<T, F> & {
  [SubKey in F]?: PeriodDateRangeValue;
};

export interface PeriodDateRangeValue {
  period?: number;
  range?: DateRangeInput;
  future?: boolean;
}

type Props = {
  future?: boolean;
  label: string;
  spacing?: number;
  value: PeriodDateRangeValue;
  onChange: (value?: PeriodDateRangeValue, range?: DateRangeInput) => void;
};
export const PeriodDateRange = ({ label, spacing = 3, value, onChange, future }: Props) => {
  const [period, setPeriod] = useState<string>(value.period ? value.period.toString() : '');
  const [type, setType] = useState<'select' | 'range' | 'input'>(() => {
    if (value.range) {
      return 'range'
    } else if (value.period && ![2, 5, 10].includes(value.period)) {
      return 'input';
    }
    return 'select'
  });
  const [range, setRange] = useState(value.range);

  useEffect(() => {
    const p = isNaN(parseFloat(period)) ? undefined : parseFloat(period);

    if (p || range) {
      onChange({
        period: p,
        range: range,
        future
      }, getDateRangeFromPeriod({period: p, range, future}))
    }
  }, [period, range, future]);

  useEffect(() => {
    if (value.period) {
      setPeriod(prev => value.period!.toString() !== prev ? value.period!.toString() : prev);
    }
    if (value.range) {
      setRange(prev => !_.isEqual(value.range, prev) ? value.range : prev);
    }
  }, [value]);

  const handleSelectChange = (value: string) => {
    if (value === 'custom') {
      setType('input');
      setPeriod(prev => !isNaN(parseFloat(prev)) ? prev : '2')
    } else if (value === 'range') {
      setType('range')
      setPeriod('range')
    } else {
      setType('select')
      setPeriod(value)
      if (!value) {
        onChange(undefined)
      }
    }
  }

  const selectValue = type !== 'input' ? period : 'custom'
  const selectorPrefix = future ? 'Next' : 'Last';

  return (
    <div>
      <SelectInput label={label}
                   hideTick
                   value={selectValue}
                   onChange={event => {
                     handleSelectChange(event.target.value as string)
                   }} >
        <MenuItem value={''}>-</MenuItem>
        <MenuItem value={'2'}>{selectorPrefix} 2 days</MenuItem>
        <MenuItem value={'5'}>{selectorPrefix} 5 days</MenuItem>
        <MenuItem value={'10'}>{selectorPrefix} 10 days</MenuItem>
        <MenuItem value={'custom'}>Custom...</MenuItem>
        <MenuItem value={'range'}>Date range</MenuItem>
      </SelectInput>
      {<Collapse in={type === 'input'} sx={{mt: type === 'input' ? spacing : 0}}>
        <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'flex-start'}>
          <Typography>{selectorPrefix}</Typography>
          <TextInput hideTick onFocus={e => e.target.select()}
                     value={period}
                     onChange={e => setPeriod(e.target.value as string)}
                     wrapperSx={{maxWidth: 60}} />
          <Typography>days</Typography>
        </Stack>
      </Collapse>}
      {<Collapse in={type === 'range'} sx={{mt: type === 'range' ? spacing : 0}}>
        <DateRange value={range}
                   label={label}
                   onChange={setRange} />
      </Collapse>}
    </div>
  );
}

export const getDateRangeFromPeriod = (value?: PeriodDateRangeValue) => {
  if (!value) return undefined;
  const {period, range, future} = value;

  if (period) {
    if (future) {
      return {
        to: moment().add(period, 'days').toISOString(),
        from: moment().toISOString()
      }
    } else {
      return {
        to: moment().toISOString(),
        from: moment().subtract(period, 'days').toISOString()
      }
    }
  } else if (range) {
    return range
  }

  return undefined
}
